import { isEqual, isSameDay } from 'date-fns';
import { type Epic, ofType } from 'redux-observable';
import { EMPTY, merge, of } from 'rxjs';
import type { AjaxResponse } from 'rxjs/ajax';
import { catchError, filter, map, mergeMap, timeout, withLatestFrom } from 'rxjs/operators';
import { v4 } from 'uuid';
import type { Rfs } from '@/models/rfs';
import { getAmericanForwardTradeCapture, getCashTradeCapture, requestForAmericanForwardQuotes, requestForCashQuotes } from '@/services/trade.service';
import { addTradeCaptureErrorAction } from '@/store/state/tradeCapture/tradeCapture.actions';
import { uuidToBase36 } from '@/utils/base36';
import { formatDateLocalized } from '@/utils/locale';
import type { AppAction } from '../app.actions';
import type { AppState } from '../app.state';
import { addErrorAction } from '../state/errors/errors.actions';
import { type RequestForStreamAction, streamRequestedAction } from '../state/rfs/rfs.actions';
import type { ProductName } from '@/models/product';

export function getNewRfsId() {
  const uuid = v4();
  return `SGM_${uuidToBase36(uuid)}`;
}

function getProductName(rfs: Rfs): ProductName {

  if (rfs.instrument === 'AmericanForward') {
    return 'FxAmericanForward'
  }

  if (rfs.postTradeOperation === 'Predeliver') {
    return 'FxPredeliver';
  }
  if (rfs.postTradeOperation === 'Rollover') {
    return 'FxSwap';
  }
  if (isSameDay(rfs.date, rfs.spotDate)) {
    return 'FxSpot';
  }

  return 'FxFwd';
}

export const requestForStreamEpic =
  (doGetCashTradeCapture = getCashTradeCapture, doGetAmericanForwardTradeCapture = getAmericanForwardTradeCapture, requestApiForCashQuotes = requestForCashQuotes, requestApiForAmericanForwardQuotes = requestForAmericanForwardQuotes): Epic<AppAction, AppAction, AppState> =>
    (action$, state$) =>
      action$.pipe(
        ofType<AppAction, RequestForStreamAction>('REQUEST_FOR_STREAM'),
        withLatestFrom(state$),
        mergeMap(([{ rfsId, retryCount }, { rfs }]) => {
          if (getProductName(rfs) !== 'FxSpot' && getProductName(rfs) !== 'FxFwd' && getProductName(rfs) !== "FxAmericanForward") {
            return of({ rfsId, isReady: true, retryCount });
          }
          const currencyPair = `${rfs.buyCurrency}/${rfs.sellCurrency}`;

          const isCash = rfs.instrument === 'Cash'
          const tradeCaptureAmericanForwardCall$ = doGetAmericanForwardTradeCapture(rfs)
          const tradeCaptureCashCall$ = doGetCashTradeCapture(getProductName(rfs), rfs.buyAmount, rfs.sellAmount, currencyPair, formatDateLocalized(rfs.date, 'yyyy-MM-dd'));
          const tradeCaptureCall$ = isCash ? tradeCaptureCashCall$ : tradeCaptureAmericanForwardCall$

          const isReady$ = tradeCaptureCall$.pipe(
            map((tradeCaptureDto) => tradeCaptureDto.changedFields.isReadyToPrice),
            filter((isReady) => isReady !== undefined && isReady !== null),
            map((isReady) => ({ rfsId, isReady: isReady!, retryCount })),
            timeout(10000),
          );

          return isReady$;
        }),
        filter(({ isReady }) => isReady === true),
        withLatestFrom(state$),
        mergeMap(([{ rfsId, retryCount }, { user, rfs, predeliver, rollover, streaming }]) => {
          const product = getProductName(rfs);
          const isCash = rfs.instrument === 'Cash'

          const requestApiForCashQuotes$ = requestApiForCashQuotes(
            rfsId,
            rfs.buyCurrency,
            rfs.sellCurrency,
            rfs[rfs.masterAmount],
            rfs.masterAmount === 'buyAmount' ? 1 : 2,
            rfs.date.toISOString(),
            product,
            user.selectedCompany!.companyBdrId,
            streaming.connectionId!,
            rfs.postTradeOperation === 'Predeliver' ? predeliver.trade?.reference
              : rfs.postTradeOperation === 'Rollover' ? rollover.trade?.reference
                : undefined,
            rollover.trade?.maturitydate,
          )

          const requestApiForAmericanForwardQuotes$ = requestApiForAmericanForwardQuotes(rfsId, user.selectedCompany!.companyBdrId, streaming.connectionId!, rfs)

          const requestApiForQuotes$ = isCash ? requestApiForCashQuotes$ : requestApiForAmericanForwardQuotes$

          return requestApiForQuotes$.pipe(
            timeout(10000),
            map((_) => streamRequestedAction(rfsId, product, retryCount)),
            catchError((error: AjaxResponse) =>
              of(
                addErrorAction({
                  message: error.responseText,
                  code: String(error.status),
                }),
              ),
            ),
          );
        }),
        // catchError((err, caught) => merge(of(addTradeCaptureErrorAction(err)), caught)),
        catchError((err, caught) => {
          return merge(of(addTradeCaptureErrorAction(err)), caught);
        }),
      );
